import Moment        from 'moment';
import env           from '$/lib/env';
import Errors        from '$/lib/Errors';
import { Country }   from '$/lib/Address';
import Validate      from '$/lib/Validate';
import externalLinks from '$/lib/externalLinks';
import { CountryLabels, CountryRegions } from '$/lib/Address';

import Permissions                    from '$/entities/lib/Permissions';
import { BaseEntity, Column, Entity } from '$/entities/BaseEntity';
import { RolePermission }             from '$/entities/roles/RolePermission';
import type { LandlordAssistFile }    from '$/entities/LandlordAssistFile';

export interface AIEvalQuestion {
	question: string;
	evaluations: { evalQuestion: string; evalResult: boolean }[];
	answer: string;
}

export interface EvaluationParams {
	country: Country;
	region: string;
	instructions: string;
	questions: AIEvalQuestion[];
	generateResponses: boolean;
}

/**
 * A file store is a collection of files that can be used as context when generating chat responses.
 * It is associated with an OpenAI vector store via the externalId property.
 * Local file stores can be created and edited; external file stores are read-only though can be used in non-prod environments.
 */
@Entity('fileStore', { common : true })
@Permissions({
	read   : Permissions.roleHasPermission(RolePermission.FileStoreRead),
	create : Permissions.roleHasPermission(RolePermission.FileStoreWrite),
	update : Permissions.roleHasPermission(RolePermission.FileStoreWrite),
	delete : Permissions.roleHasPermission(RolePermission.FileStoreWrite),
})
export abstract class FileStore extends BaseEntity {

	constructor(partial?: Partial<FileStore>) {
		super();
		this.mergeColumns(partial);
	}

	@Column()
	@Validate({ required : true, minLength : 3 })
	name: string = '';

	/**
	 * Indicates that this is the file store whose files to use as context when generating chat responses.
	 * Only one file store can be live at a time.
	 */
	@Column()
	@Permissions({ write : Permissions.serverOnly })
	isLive: boolean = false;

	@Column()
	@Permissions({ write : Permissions.serverOnly })
	externalId: string = '';

	@Column('text')
	@Permissions({
		read  : Permissions.roleHasPermission(RolePermission.SystemPromptRead),
		write : Permissions.roleHasPermission(RolePermission.SystemPromptWrite),
	})
	@Validate({ required : true })
	systemPromptTemplate: string = '';

	get externalName() {
		return env.getEnvironmentPrefixedText(this.name);
	}

	/**
	 * If true, this file store does not exist in the local DB and cannot be updated or deleted.
	 * It can only be used to query it and run chat responses and evaluations against it.
	 */
	get isExternal() {
		return this.id.startsWith('external-');
	}

	get externalLink() {
		return `https://platform.openai.com/storage/vector_stores/${this.externalId}`;
	}

	get canModify() {
		return !this.isLive && !this.isExternal;
	}

	/**
	 * Throws an error if the file store cannot be modified.
	 */
	ensureCanModify() {
		if (this.isLive) {
			throw new Error('Cannot update live file store');
		}

		if (this.isExternal) {
			throw new Error('Cannot update external file store');
		}
	}

	/**
	 * Gets the system prompt from the template by replacing parameters with their values and handling conditional blocks.
	 * The template can have the following parameters: {{country}}, {{region}}, {{pricing}}, and {{timestamp}}.
	 * It also supports country-specific conditional blocks in the format {{country=XX|Text for country XX}}.
	 *
	 * The method will:
	 * - Replace conditional blocks based on the country
	 * - Replace standard parameters with their values
	 * - Clean up unnecessary whitespace and newlines
	 * - Validate that all parameters were handled correctly
	 *
	 * @param options - The options object containing country and region
	 * @param options.country - The country code
	 * @param options.region - The name of the region (province/state)
	 * @param options.template - An override for the template to use for the system prompt. If not provided, defaults to the one in the file store.
	 * @returns The processed system prompt with all parameters replaced
	 * @throws {Error} If there are unknown parameters or the template is incorrectly formatted
	 */
	getFormattedInstructions(options: { country: Country; region: string; template?: string }): string {
		let prompt = options.template || this.systemPromptTemplate;

		// Handle conditional blocks first (thanks Claude for the regex)
		// This handles country or region-specific blocks like {{country=CA|Canada specific rules}}
		// If the country or region matches, we render the text, otherwise it's removed entirely
		const applyConditional = (str: string, type: 'country' | 'region', selected: string) => {
			const regex = new RegExp(`{{\\s*${type}=([^|]+?)\\s*\\|\\s*([\\s\\S]*?)\\s*}}`, 'g');

			return str.replace(regex, (match, value, text) => {
				const isValid = type === 'country'
					? _.values(Country).includes(value as Country)
					: _.values(CountryRegions).some(regions => value in regions);

				if (!isValid) {
					throw new Error(`Invalid ${type} value in conditional block: ${value}`);
				}

				return selected === value ? text : '';
			});
		};

		prompt = applyConditional(prompt, 'country', options.country);
		prompt = applyConditional(prompt, 'region', options.region);

		// Then handle the rest of the parameters
		const paramMapping = {
			country : CountryLabels[options.country],
			region  : CountryRegions[options.country][options.region],
			pricing : externalLinks.pricing[options.country],
			date    : Moment().format('YYYY-MM-DD'),
		};

		for (const [ param, value ] of Object.entries(paramMapping)) {
			prompt = prompt.replace(new RegExp(`{{\\s*${param}\\s*}}`, 'g'), value);
		}

		// Replace 3 or more consecutive newlines with exactly 2 newlines
		prompt = prompt.replace(/\n{3,}/g, '\n\n');

		// If there's any unhandled or ill-defined parameters, throw an error
		const matches = prompt.match(/{{(.*?)}}/g);
		if (matches) {
			const unknownParam = matches[0].slice(2, -2);

			if (unknownParam.startsWith('country=') || unknownParam.startsWith('region=')) {
				throw new Errors.HTTP.BadRequest(`Incorrectly formatted conditional block: {{${unknownParam}}}. Add some text after the | or remove it.`);
			}
			throw new Errors.HTTP.BadRequest(`Unknown parameter: {{${unknownParam}}}`);
		}

		if (prompt.includes('{{') || prompt.includes('}}')) {
			throw new Errors.HTTP.BadRequest('Incorrectly formatted system prompt template');
		}

		return prompt;
	}

	abstract getFiles(options: { lastFile?: LandlordAssistFile | string; limit?: number }): Promise<LandlordAssistFile[]>;

	/**
	 * @returns All of the unique file's country/regions for files in this file store.
	 */
	getAvailableRegions(): Promise<{ country: Country; region: string }[]> {
		return (this.constructor as typeof FileStore).getAvailableRegions(this);
	}

	/**
	 * Add the given (already uploaded) files to the file store.
	 * Note that live file stores cannot be updated.
	 */
	abstract addFiles(files: PossibleArray<LandlordAssistFile>): Promise<void>;

	/**
	 * Copy the files from the given file store to this file store.
	 * Note that live file stores cannot be updated.
	 */
	abstract addFilesFromFileStore(sourceFileStore: FileStore): Promise<void>;

	/**
	 * Remove the given files from the file store.
	 * Note that live file stores cannot be updated.
	 */
	abstract removeFiles(files: PossibleArray<LandlordAssistFile>): Promise<void>;

	abstract runEvaluation(params: EvaluationParams): Promise<AIEvalQuestion[]>;

	/**
	 * Attempts to set this file store as live which will be used as context when generating chat responses.
	 * If this file store is already live, this will throw an error.
	 * If this file store is external (ie. not local), this will throw an error.
	 */
	abstract publish(): Promise<void>;

	/**
	 * Get the number of files in the file store.
	 */
	abstract getNumFiles(): Promise<number>;

	static getLiveStore<T extends typeof FileStore>(this: T): Promise<InstanceType<T>> {
		return this.findOne({ where : { isLive : true } }) as Promise<InstanceType<T>>;
	}

	static async getAvailableRegions(fileStore?: FileStore): Promise<{ country: Country; region: string }[]> { // eslint-disable-line @typescript-eslint/no-unused-vars
		throw new Errors.NotImplemented();
	}

	static async findByExternalId(externalId: string): Promise<FileStore> { // eslint-disable-line @typescript-eslint/no-unused-vars
		throw new Errors.NotImplemented();
	}

	/**
	 * @returns All external file stores.
	 */
	static async findAllExternal(): Promise<FileStore[]> { // eslint-disable-line @typescript-eslint/no-unused-vars
		throw new Errors.NotImplemented();
	}

}
